import { Key } from 'react';
import { Selection } from '@react-types/shared';

import { areArraysEqual, areSetsEqual, parseValueFromKey } from '@xemplo/common-util';
import { ListItem } from '@xemplo/listbox';

import { TKey } from './text-dropdown-multi-v2.types';

export function isFieldValueEqual(fieldValue: unknown, selectedKeys: Selection): boolean {
  let fieldValueKeys: Set<Key>;
  if (!fieldValue) {
    fieldValueKeys = new Set<Key>();
  } else {
    fieldValueKeys = new Set<Key>(Array.isArray(fieldValue) ? fieldValue : [fieldValue]);
  }

  return areSetsEqual(fieldValueKeys, selectedKeys as Set<Key>);
}

export function isEqual<T extends ListItem>(
  current: TKey<T> | TKey<T>[],
  values: Key | Key[]
): boolean {
  const normalize = (v: unknown) => (Array.isArray(v) ? v.map(String) : [String(v)]);
  const currentNormalized = normalize(current);
  const valuesNormalized = normalize(values);
  return areArraysEqual(currentNormalized, valuesNormalized);
}

/**
 * This function is used to handle the selection of items in the dropdown.
 * It will return the selected keys in the format that is expected by the
 * react-hook-form. It will also handle the case where the user selects
 * the "select all" option.
 */
export function selectionHandler<T extends ListItem>(
  selection: Selection,
  items: T[],
  isMulti: boolean
) {
  // This is needed in case a dropdown single contains a key called 'all'
  // and it address the desambiguation of the key from the select all option
  if (selection === 'all') return isMulti ? items.map((item) => item.key) : 'all';

  const values = Array.from(selection).map(parseValueFromKey);
  if (isMulti) return values;
  return values[0];
}

/**
 * Annoyingly, the react-aria has an issue where it automatically
 * coerces the keys into strings. With the react-hook-form, we want
 * the values to be correct types to avoid having to manually parse
 * on every implementation or forced to stringify values that really
 * should be numbers. This function will be used to convert the
 * values into strings to be compatible with the react-aria collection
 */
export function reactAriafyValue<T extends ListItem>(value: TKey<T>): string[] {
  return Array.isArray(value) ? value.map((v) => String(v)) : [String(value)];
}
